"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { encodeCheckoutToken } from "../../lib/checkoutToken";

export const dynamic = "force-dynamic";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000";

type EnterprisePartner = {
  id?: number;
  enterpriseId: string;
  name: string;
  feePercentage: number;
  feeBps: number;
  payoutWallet?: string;
  finixMerchantId?: string;
  contactEmail?: string;
  active?: boolean;
};

const DEFAULT_PARTNERS: EnterprisePartner[] = [
  {
    enterpriseId: "EP-SHOPIFY-01",
    name: "Shopify Enterprise POS",
    feePercentage: 2.0,
    feeBps: 200,
    payoutWallet: "0x71C8F7960D8e6f1165A6836c01eC823725F7f474",
    finixMerchantId: "MU_ShopifyEnterprise01",
    contactEmail: "integrations@shopify-partner.example",
    active: true,
  },
  {
    enterpriseId: "EP-LIGHTSPEED-02",
    name: "Lightspeed Commerce Partner",
    feePercentage: 2.25,
    feeBps: 225,
    payoutWallet: "0x82C74b4A9A6b83f054238e55eE9a83B642139e8B",
    finixMerchantId: "MU_LightspeedEnterprise02",
    contactEmail: "partners@lightspeed.example",
    active: true,
  },
  {
    enterpriseId: "EP-SQUARE-03",
    name: "Square Enterprise Solutions",
    feePercentage: 1.75,
    feeBps: 175,
    payoutWallet: "0x93D8c12a7810A2B93D4581C6026ec17D91f7a01C",
    finixMerchantId: "MU_SquareEnterprise03",
    contactEmail: "enterprise@squareup.example",
    active: true,
  },
  {
    enterpriseId: "EP-CLOVER-04",
    name: "Clover Partner Network",
    feePercentage: 2.5,
    feeBps: 250,
    payoutWallet: "0xa1B2c3D4e5F60718293a4b5c6d7e8f9012345678",
    finixMerchantId: "MU_CloverEnterprise04",
    contactEmail: "pos@clover-partner.example",
    active: true,
  },
  {
    enterpriseId: "EP-REBOOKED-05",
    name: "Rebooked Enterprise POS",
    feePercentage: 2.0,
    feeBps: 200,
    payoutWallet: "0xb2C3d4E5f60718293a4B5c6D7e8F90123456789A",
    finixMerchantId: "MU_RebookedEnterprise05",
    contactEmail: "integrations@rebooked.example",
    active: true,
  },
];

export default function AdminEnterprisePartnersPage() {
  const [partners, setPartners] = useState<EnterprisePartner[]>(DEFAULT_PARTNERS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form State for creating/editing partner
  const [enterpriseId, setEnterpriseId] = useState("");
  const [name, setName] = useState("");
  const [feePercentage, setFeePercentage] = useState("2.00");
  const [payoutWallet, setPayoutWallet] = useState("");
  const [finixMerchantId, setFinixMerchantId] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  // POS Generator State
  const [selectedEnterprise, setSelectedEnterprise] =
    useState("EP-SHOPIFY-01");
  const [agreementAddress, setAgreementAddress] = useState(
    "0x16832B6029Ec8EC20E3Aa0007E8986a81Ae2902d"
  );
  const [customerWallet, setCustomerWallet] = useState(
    "0x3C2F69Ca6f18c00bb0EBcBa93Fd8252110A3992F"
  );
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [generatedIframe, setGeneratedIframe] = useState<string | null>(null);
  const [sampleAmount, setSampleAmount] = useState<number>(100);

  const fetchPartners = async () => {
    try {
      setError(null);
      const res = await fetch(`${API_BASE_URL}/api/pos/enterprise-partners`, {
        cache: "no-store",
      });
      if (res.ok) {
        const data = await res.json();
        if (data.partners && data.partners.length > 0) {
          setPartners(data.partners);
        }
      }
    } catch (err) {
      console.warn("Using default enterprise partners registry:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchPartners();
  }, []);

  const handleSavePartner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enterpriseId.trim() || !name.trim()) {
      setError("Enterprise Code and Company Name are required.");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const res = await fetch(`${API_BASE_URL}/api/pos/enterprise-partners`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          enterpriseId: enterpriseId.trim().toUpperCase(),
          name: name.trim(),
          feePercentage: Number(feePercentage),
          payoutWallet: payoutWallet.trim() || undefined,
          finixMerchantId: finixMerchantId.trim() || undefined,
          contactEmail: contactEmail.trim() || undefined,
          active: true,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to save enterprise partner.");
      }

      setSuccess(
        `Enterprise Partner ${enterpriseId.toUpperCase()} saved successfully!`
      );
      setEnterpriseId("");
      setName("");
      setFeePercentage("2.00");
      setPayoutWallet("");
      setFinixMerchantId("");
      setContactEmail("");

      await fetchPartners();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Unable to save enterprise partner."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateLink = (e: React.FormEvent) => {
    e.preventDefault();
    const portalBase =
      typeof window !== "undefined"
        ? window.location.origin
        : "http://localhost:3001";

    const token = encodeCheckoutToken(agreementAddress, customerWallet);
    const link = `${portalBase}/pos/checkout?token=${token}`;
    const iframe = `<iframe src="${link}" width="100%" height="680" frameborder="0" allow="payment"></iframe>`;

    setGeneratedLink(link);
    setGeneratedIframe(iframe);
  };

  const activePartner =
    partners.find(
      (p) =>
        p.enterpriseId.toLowerCase() === selectedEnterprise.toLowerCase()
    ) ?? partners[0];

  const enterpriseFeeRate = activePartner?.feePercentage ?? 2.0;
  const finixFeeCalc = Number((sampleAmount * 0.029 + 0.3).toFixed(2));
  const enterpriseFeeCalc = Number(
    ((sampleAmount * enterpriseFeeRate) / 100).toFixed(2)
  );
  const agpsiraFeeCalc = Number((sampleAmount * 0.012).toFixed(2));
  const netResidualDeposit = Number(
    (sampleAmount - finixFeeCalc - enterpriseFeeCalc - agpsiraFeeCalc).toFixed(
      2
    )
  );

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="mb-2 inline-block rounded-full bg-blue-500/20 px-3 py-1 text-xs font-semibold text-blue-300">
              Administrator Platform Controls
            </div>
            <h1 className="text-4xl font-bold md:text-6xl">
              Enterprise Software Partners
            </h1>
            <p className="mt-2 text-lg text-slate-300">
              Manage enterprise software identification codes, pre-negotiated platform fees, and embeddable POS checkout links.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              href="/admin/platform-role-wallets"
              className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-medium text-slate-200 hover:border-slate-500"
            >
              Role Wallets
            </Link>
            <Link
              href="/admin/dashboard"
              className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-medium text-slate-200 hover:border-slate-500"
            >
              Admin Dashboard
            </Link>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-2xl border border-red-500/50 bg-red-500/10 p-4 text-sm text-red-200">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 rounded-2xl border border-emerald-500/50 bg-emerald-500/10 p-4 text-sm text-emerald-200">
            {success}
          </div>
        )}

        {/* Top Metric Cards */}
        <div className="mb-12 grid gap-6 md:grid-cols-3">
          <div className="rounded-3xl border border-slate-800 bg-slate-900 p-8">
            <p className="mb-2 text-slate-400">Registered Enterprise Partners</p>
            <h2 className="text-4xl font-bold text-blue-400">{partners.length}</h2>
            <p className="mt-1 text-xs text-slate-400">
              Integrated POS and software platforms
            </p>
          </div>

          <div className="rounded-3xl border border-slate-800 bg-slate-900 p-8">
            <p className="mb-2 text-slate-400">AGPSIRA Platform Deposit</p>
            <h2 className="text-4xl font-bold text-emerald-400">1.20%</h2>
            <p className="mt-1 text-xs text-slate-400">
              Auto-routed to RevenueDistributor
            </p>
          </div>

          <div className="rounded-3xl border border-slate-800 bg-slate-900 p-8">
            <p className="mb-2 text-slate-400">Multi-Fee Automation</p>
            <h2 className="text-xl font-bold text-violet-400">4-Way Split</h2>
            <p className="mt-1 text-xs text-slate-400">
              Finix + Enterprise + AGPSIRA + PSE
            </p>
          </div>
        </div>

        {/* Registered Partners Table */}
        <div className="mb-12">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-2xl font-bold">Enterprise Partner Registry</h2>
            <span className="text-xs text-slate-400">
              Identifies enterprise software platforms on POS transactions
            </span>
          </div>

          <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900">
            <div className="hidden md:grid md:grid-cols-6 bg-slate-800 p-5 font-semibold text-slate-100">
              <div>Enterprise ID</div>
              <div>Partner Name</div>
              <div>Fee Rate</div>
              <div>Payout Wallet</div>
              <div>Finix Merchant ID</div>
              <div>Status</div>
            </div>

            {loading ? (
              <div className="p-8 text-center text-slate-400">
                Loading enterprise partners...
              </div>
            ) : partners.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                No enterprise partners registered yet. Add one below.
              </div>
            ) : (
              partners.map((partner) => (
                <div
                  key={partner.enterpriseId}
                  className="grid gap-4 border-t border-slate-800 p-6 md:grid-cols-6 md:p-5"
                >
                  <div className="font-mono text-xs font-bold text-blue-300">
                    {partner.enterpriseId}
                  </div>
                  <div className="font-medium text-white">{partner.name}</div>
                  <div className="font-semibold text-emerald-400">
                    {partner.feePercentage.toFixed(2)}% ({partner.feeBps} BPS)
                  </div>
                  <div className="break-all font-mono text-xs text-slate-400">
                    {partner.payoutWallet || "Not assigned"}
                  </div>
                  <div className="font-mono text-xs text-violet-300">
                    {partner.finixMerchantId || "Direct Payout"}
                  </div>
                  <div>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        partner.active
                          ? "bg-emerald-500/20 text-emerald-300"
                          : "bg-amber-500/20 text-amber-300"
                      }`}
                    >
                      {partner.active ? "Active" : "Disabled"}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Two-Column: Register New Partner & POS Link Generator */}
        <div className="grid gap-8 lg:grid-cols-2">
          {/* Form: Add / Update Enterprise Partner */}
          <div className="rounded-3xl border border-slate-800 bg-slate-900 p-8">
            <h2 className="text-2xl font-bold mb-2">Register Enterprise Partner</h2>
            <p className="text-sm text-slate-400 mb-6">
              Establish a software partner with their fee percentage and payout credentials.
            </p>

            <form onSubmit={handleSavePartner} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-300">
                    Enterprise ID / Code *
                  </label>
                  <input
                    required
                    value={enterpriseId}
                    onChange={(e) => setEnterpriseId(e.target.value)}
                    placeholder="EP-SHOPIFY-01"
                    className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-sm text-white outline-none focus:border-blue-500 font-mono uppercase"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-300">
                    Company Name *
                  </label>
                  <input
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Shopify Enterprise"
                    className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-300">
                    Enterprise Fee (%) *
                  </label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    min="0"
                    max="50"
                    value={feePercentage}
                    onChange={(e) => setFeePercentage(e.target.value)}
                    placeholder="2.00"
                    className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-300">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="partner@company.com"
                    className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-300">
                  Payout Crypto Wallet Address
                </label>
                <input
                  value={payoutWallet}
                  onChange={(e) => setPayoutWallet(e.target.value)}
                  placeholder="0x..."
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-sm text-white outline-none focus:border-blue-500 font-mono"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-300">
                  Finix Merchant ID
                </label>
                <input
                  value={finixMerchantId}
                  onChange={(e) => setFinixMerchantId(e.target.value)}
                  placeholder="MU_..."
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-sm text-white outline-none focus:border-blue-500 font-mono"
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full rounded-xl bg-blue-600 py-3 font-semibold text-white hover:bg-blue-500 disabled:opacity-50 transition"
              >
                {saving ? "Saving Partner..." : "Save Enterprise Partner"}
              </button>
            </form>
          </div>

          {/* Tool: Generate Embed POS Link */}
          <div className="rounded-3xl border border-slate-800 bg-slate-900 p-8">
            <h2 className="text-2xl font-bold mb-2">
              Generate POS Payment / Embed Link
            </h2>
            <p className="text-sm text-slate-400 mb-6">
              Create a pre-bound POS link embedded with the enterprise partner and PSE agreement.
            </p>

            <form onSubmit={handleGenerateLink} className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-300">
                  Select Enterprise Software Partner
                </label>
                <select
                  value={selectedEnterprise}
                  onChange={(e) => setSelectedEnterprise(e.target.value)}
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                >
                  {partners.map((p) => (
                    <option key={p.enterpriseId} value={p.enterpriseId}>
                      {p.name} ({p.enterpriseId}) — {p.feePercentage.toFixed(2)}%
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-300">
                  Merchant PSE Agreement Contract Address
                </label>
                <input
                  required
                  value={agreementAddress}
                  onChange={(e) => setAgreementAddress(e.target.value)}
                  placeholder="0x..."
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-sm text-white outline-none focus:border-blue-500 font-mono"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-300">
                  Merchant Customer / PSE Wallet Address
                </label>
                <input
                  required
                  value={customerWallet}
                  onChange={(e) => setCustomerWallet(e.target.value)}
                  placeholder="0x..."
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-sm text-white outline-none focus:border-blue-500 font-mono"
                />
              </div>

              <button
                type="submit"
                className="w-full rounded-xl bg-emerald-600 py-3 font-semibold text-white hover:bg-emerald-500 transition shadow-lg shadow-emerald-600/30"
              >
                Generate Enterprise POS Embed Link
              </button>
            </form>

            {/* Generated Output */}
            {generatedLink && (
              <div className="mt-6 space-y-4 border-t border-slate-800 pt-6">
                <div>
                  <span className="text-xs font-semibold text-emerald-300 uppercase tracking-wider">
                    Embeddable POS Link:
                  </span>
                  <div className="mt-1 rounded-xl bg-slate-950 p-3 font-mono text-xs text-blue-300 break-all border border-slate-800 select-all">
                    {generatedLink}
                  </div>
                </div>

                <div>
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    IFrame Embed Snippet:
                  </span>
                  <textarea
                    readOnly
                    rows={2}
                    value={generatedIframe ?? ""}
                    className="mt-1 w-full rounded-xl bg-slate-950 p-3 font-mono text-xs text-slate-300 border border-slate-800 select-all"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Live Simulation Calculator */}
        <div className="mt-12 rounded-3xl border border-slate-800 bg-slate-900 p-8">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-bold">
                Live Multi-Fee Deposit Simulation
              </h2>
              <p className="text-sm text-slate-400">
                Visual preview of the automated 4-way fee and dual smart contract routing.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">Sample POS Gross:</span>
              <input
                type="number"
                value={sampleAmount}
                onChange={(e) => setSampleAmount(Math.max(1, Number(e.target.value)))}
                className="w-28 rounded-xl border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm font-bold text-white text-right"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
              <span className="text-xs uppercase text-slate-400">1. Finix Fee (2.9% + 30¢)</span>
              <div className="mt-2 text-2xl font-bold text-amber-400">
                ${finixFeeCalc.toFixed(2)}
              </div>
              <p className="mt-1 text-[11px] text-slate-500">Card processing</p>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
              <span className="text-xs uppercase text-slate-400">
                2. Enterprise Fee ({enterpriseFeeRate}%)
              </span>
              <div className="mt-2 text-2xl font-bold text-violet-400">
                ${enterpriseFeeCalc.toFixed(2)}
              </div>
              <p className="mt-1 text-[11px] text-slate-500">{activePartner?.name ?? "Enterprise"}</p>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
              <span className="text-xs uppercase text-slate-400">3. AGPSIRA Fee (1.20%)</span>
              <div className="mt-2 text-2xl font-bold text-blue-400">
                ${agpsiraFeeCalc.toFixed(2)}
              </div>
              <p className="mt-1 text-[11px] text-blue-300">➔ RevenueDistributor.sol</p>
            </div>

            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5">
              <span className="text-xs uppercase text-emerald-300">4. Net PSE Deposit</span>
              <div className="mt-2 text-2xl font-bold text-emerald-400">
                ${netResidualDeposit.toFixed(2)}
              </div>
              <p className="mt-1 text-[11px] text-emerald-300">➔ AGPSIRAAgreement.sol</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
